import { createContext, useContext, PropsWithChildren, useEffect, useMemo } from 'react';
import { PREFERENCE_KEYS } from 'types';
import { useFiltersContext } from './filtersContext';
import { FilterChipsState } from '../hooks/useFilterChipsState';
import { useSavedFilters } from '../hooks/useSavedFilters';

type SavedFilterContextValue = {
  filterChips: FilterChipsState;
  savedFilters: ReturnType<typeof useSavedFilters>;
};

const SavedFilterContext = createContext<SavedFilterContextValue>({
  filterChips: { hide: () => {}, show: () => {}, isShown: false },
  savedFilters: {
    activeFilterData: undefined,
    preferenceColumns: [],
    activeSavedFilterId: undefined,
    createOrgSavedFilter: () => Promise.reject(),
    deleteSavedFilter: () => Promise.reject(),
    createSavedFilter: () => Promise.reject(),
    defaultSavedFilterId: undefined,
    deleteOrgSavedFilter: () => Promise.reject(),
    loading: false,
    orgValue: {},
    editOrgSavedFilter: () => Promise.reject(),
    editSavedFilter: () => Promise.reject(),
    setActiveSavedFilter: () => Promise.reject(),
    setDefaultSavedFilter: () => Promise.reject(),
    updateOrgSavedFilter: () => Promise.reject(),
    updateSavedFilter: () => Promise.reject(),
    value: {},

    showFilterChips: false,
    filterChipsHide: () => {},
    filterChipsShow: () => {},
  },
});
export const useSavedFilterContext = () => {
  const context = useContext(SavedFilterContext);
  if (!context) {
    throw new Error('useSavedFilterContext must be used within a SavedFilterContext.Provider');
  }
  return context;
};

export const SavedFilterProvider = ({
  children,
  preferenceKey,
}: PropsWithChildren<{ preferenceKey?: PREFERENCE_KEYS }>) => {
  const savedFilters = useSavedFilters({ preferenceKey });

  const { setActiveSavedFilter } = savedFilters;
  const { clearActiveSavedFilter } = useFiltersContext();

  useEffect(
    () => {
      if (clearActiveSavedFilter) {
        setActiveSavedFilter(undefined);
      }
    },
    // exclude setActiveSavedFilter because it causes this to execute an extra time, after we change the active saved filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearActiveSavedFilter]
  );

  const filterChips = useMemo(() => {
    return {
      isShown: savedFilters.showFilterChips,
      hide: savedFilters.filterChipsHide,
      show: savedFilters.filterChipsShow,
    };
  }, [savedFilters.showFilterChips, savedFilters.filterChipsHide, savedFilters.filterChipsShow]);
  return (
    <SavedFilterContext.Provider value={{ filterChips, savedFilters }}>
      {children}
    </SavedFilterContext.Provider>
  );
};
